import { SkeletonProps } from '@mui/material';
import MuiTypography, {
    TypographyProps as MuiTypographyProps
} from '@mui/material/Typography';
import { SystemCssProperties } from '@mui/system/styleFunctionSx';
import * as React from 'react';

import { Skeleton } from '../skeleton';
import { ColorProp, usePaletteColor } from '../theme';
import { withVersion } from '../utils';

export * from './measure';
export * from './with-tooltip';

export const typographyVariants = [
    'headingLarge',
    'headingMedium',
    'headingSmall',
    'bodyDefault',
    'bodyEmphasis',
    'bodyStrong',
    'detail',
    'code'
] as const;

export type TypographyVariant = (typeof typographyVariants)[number];

export type TypographyTextTransform =
    | React.CSSProperties['textTransform']
    | 'sentence';

export const typographyTextTransforms = [
    'none',
    'capitalize',
    'uppercase',
    'lowercase',
    'sentence',
    'full-width',
    'full-size-kana',
    'inherit',
    'initial',
    'unset',
    'revert',
    'revert-layer',
    'unset'
] as const satisfies TypographyTextTransform[];

type TypographyStyleProps = Pick<
    SystemCssProperties,
    | 'display'
    | 'height'
    | 'flex'
    | 'fontSize'
    | 'gridColumn'
    | 'lineHeight'
    | 'minWidth'
    | 'minHeight'
    | 'overflow'
    | 'paddingLeft'
    | 'textOverflow'
    | 'whiteSpace'
    | 'mt'
    | 'mb'
    | 'ml'
    | 'mr'
    | 'mx'
    | 'px'
    | 'py'
    | 'marginLeft'
    | 'marginTop'
    | 'marginBottom'
    | 'fontStyle'
    | 'fontWeight'
    | 'letterSpacing'
    | 'textAlign'
    | 'visibility'
    | 'borderRadius'
    | 'bgcolor'
>;

export interface TypographyProps
    extends TypedOmit<MuiTypographyProps, 'color' | 'variant'>,
        TypographyStyleProps {
    variant?: TypographyVariant;
    color?: ColorProp;
    maxWidth?: React.CSSProperties['maxWidth'];
    fontWeight?: React.CSSProperties['fontWeight'];
    lineClamp?: number;
    textTransform?: TypographyTextTransform;
    skeleton?: SkeletonProps | boolean | string | number;
}

const TypographyImpl = React.forwardRef<HTMLSpanElement, TypographyProps>(
    (
        {
            variant,
            align = 'left',
            maxWidth = '100%',
            color,
            bgcolor,
            display,
            height,
            flex,
            fontSize,
            fontStyle,
            fontWeight,
            gridColumn,
            lineHeight,
            lineClamp,
            marginBottom,
            marginLeft,
            marginTop,
            minWidth,
            minHeight,
            mt,
            mb,
            ml,
            mr,
            px,
            py,
            letterSpacing,
            overflow,
            paddingLeft,
            textTransform,
            textAlign,
            textOverflow,
            whiteSpace,
            visibility,
            borderRadius,
            skeleton,
            children,
            ...muiProps
        },
        ref
    ) => {
        const paletteColor = usePaletteColor(color);

        const cssLineClamp = (() => {
            if (lineClamp) {
                return getSxLineClamp(lineClamp);
            }

            return undefined;
        })();

        const cssTextTransform =
            textTransform === 'sentence' ? undefined : textTransform;

        const resolvedVariant =
            variant ?? ('body2' as MuiTypographyProps['variant']);

        const component = (
            <MuiTypography
                ref={ref}
                variant={resolvedVariant}
                variantMapping={{
                    headingLarge: 'h2',
                    headingMedium: 'h3',
                    headingSmall: 'h4',
                    bodyDefault: 'p',
                    bodyEmphasis: 'p',
                    bodyStrong: 'p',
                    detail: 'span',
                    code: 'code'
                }}
                align={align}
                {...muiProps}
                sx={{
                    fontFamily: (theme) => theme.typography.fontFamily,
                    color: paletteColor,
                    bgcolor,
                    display,
                    height,
                    flex,
                    fontSize,
                    fontWeight,
                    fontStyle,
                    gridColumn,
                    lineHeight,
                    marginBottom,
                    marginLeft,
                    marginTop,
                    maxWidth,
                    minWidth,
                    minHeight,
                    mt,
                    mb,
                    mr,
                    ml,
                    px,
                    py,
                    letterSpacing,
                    overflow,
                    paddingLeft,
                    textAlign,
                    textOverflow: textOverflow ?? 'ellipsis',
                    textTransform: cssTextTransform,
                    whiteSpace,
                    visibility,
                    borderRadius,
                    ...cssLineClamp,
                    ...muiProps.sx
                }}
            >
                {prepareTextTransform(children, textTransform)}
            </MuiTypography>
        );

        if (skeleton) {
            return (
                <Skeleton {...(typeof skeleton === 'object' ? skeleton : {})}>
                    {component}
                </Skeleton>
            );
        }

        return component;
    }
);

export const Typography = withVersion(TypographyImpl, '1.0.0');

export function getSxLineClamp(lines: number) {
    return {
        lineClamp: `${lines}`,
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical'
    } satisfies TypographyProps['sx'];
}

export const firstLetterUppercase = {
    ':first-letter': { textTransform: 'uppercase' }
} as const;

export const prepareTextTransform = <T extends React.ReactNode>(
    element: T,
    textTransform: TypographyTextTransform | undefined
): T => {
    if (textTransform !== 'sentence') {
        return element;
    }
    return sentence(element);
};

export const sentence = <T extends React.ReactNode>(element: T): T => {
    if (!element) {
        return element;
    }
    if (typeof element === 'string') {
        return `${element[0].toUpperCase()}${element.slice(1)}` as T;
    }
    return element;
};
